import { useEffect } from 'react';
import { toast } from 'react-toastify';

const useDashboardEffects = ({
  user,
  guicheSelecionado,
  buscarSenhas,
  buscarEstatisticas,
  buscarSenhaEmAtendimento,
  atendimentoIniciado,
  senhaChamada,
  atendimentoFinalizado,
  tempoAtendimento,
  salvarEstadoAtendimento,
  setGuicheSelecionado
}) => {

  // Efeito para buscar a senha em atendimento pelo usuário atual ao carregar a página
  useEffect(() => {
    if (user && user.id) {
      // Sempre verificar no servidor primeiro para garantir dados atualizados
      buscarSenhaEmAtendimento();
      
      // Verificar se existe estado salvo no localStorage como backup
      const estadoSalvo = localStorage.getItem(`atendimento_${user.id}`);
      
      if (estadoSalvo) {
        try {
          const dados = JSON.parse(estadoSalvo);
          
          // Verificar se os dados são válidos e não estão expirados (12 horas max)
          const agora = new Date();
          const dataInicio = dados.iniciadoEm ? new Date(dados.iniciadoEm) : new Date(dados.timestamp);
          const diferencaHoras = (agora - dataInicio) / (1000 * 60 * 60);
          
          if (diferencaHoras >= 12 || dados.finalizado) {
            // Se os dados estiverem expirados ou finalizados, limpar o localStorage
            localStorage.removeItem(`atendimento_${user.id}`);
          }
        } catch (error) {
          console.error('Erro ao verificar atendimento salvo:', error);
          localStorage.removeItem(`atendimento_${user.id}`);
        }
      }
    }
  }, [user, buscarSenhaEmAtendimento]);

  // Efeito para buscar senhas periodicamente
  useEffect(() => {
    console.log('🔄 Configurando intervalos de atualização...');
    
    // Busca inicial
    buscarSenhas();
    buscarEstatisticas();

    // Configurar intervalo para atualização a cada 30 segundos
    const intervaloSenhas = setInterval(() => {
      console.log('⏰ Intervalo de senhas disparado - verificando mudanças...');
      buscarSenhas();
    }, 30000);
    
    // Configurar intervalo para atualização de estatísticas a cada 60 segundos
    const intervaloEstatisticas = setInterval(() => {
      console.log('📊 Intervalo de estatísticas disparado - verificando mudanças...');
      buscarEstatisticas();
    }, 60000);

    // Cleanup dos intervalos quando o componente for desmontado
    return () => {
      console.log('🧹 Limpando intervalos...');
      clearInterval(intervaloSenhas);
      clearInterval(intervaloEstatisticas);
    };
  }, [guicheSelecionado, user, buscarSenhas, buscarEstatisticas]);

  // Efeito para detectar a atualização do usuário no contexto de autenticação
  useEffect(() => {
    if (user && user.servicos_atendidos) {
      console.log('👤 Usuário ou serviços atualizados - recarregando senhas...', user.servicos_atendidos);
      buscarSenhas();
    }
  }, [user?.servicos_atendidos, buscarSenhas]);

  // Efeito para salvar periodicamente o estado do atendimento no localStorage
  useEffect(() => {
    // Não fazer nada se não houver atendimento em andamento
    if (!atendimentoIniciado || !senhaChamada || !user?.id) return;
    
    // Criar intervalo para salvar o estado no localStorage a cada 10 segundos
    const intervalId = setInterval(() => {
      // Obter o estado atual do localStorage para preservar a data de início
      const estadoSalvo = localStorage.getItem(`atendimento_${user.id}`);
      let iniciadoEm = null;
      
      if (estadoSalvo) {
        try {
          const dadosAnteriores = JSON.parse(estadoSalvo);
          // Preservar a hora original de início
          if (dadosAnteriores.iniciadoEm) {
            iniciadoEm = dadosAnteriores.iniciadoEm;
          }
        } catch (error) {
          console.error('Erro ao ler dados salvos:', error);
        }
      }
      
      // Se não tem data de início salva, usar a data atual
      if (!iniciadoEm) {
        iniciadoEm = new Date().toISOString();
      }
      
      // Atualizar o localStorage
      const dados = {
        senha: senhaChamada,
        iniciado: atendimentoIniciado,
        finalizado: atendimentoFinalizado,
        iniciadoEm: iniciadoEm,
        tempoDecorrido: tempoAtendimento,
        timestamp: new Date().toISOString()
      };
      
      localStorage.setItem(`atendimento_${user.id}`, JSON.stringify(dados));
    }, 10000); // A cada 10 segundos
    
    // Limpeza ao desmontar
    return () => {
      clearInterval(intervalId);
    };
  }, [atendimentoIniciado, senhaChamada, atendimentoFinalizado, user, tempoAtendimento]);

  // Efeito para configurar o handler de fechamento de página
  useEffect(() => {
    // Handler para salvar o estado antes da página ser fechada/atualizada
    const handleBeforeUnload = () => {
      if (senhaChamada && !atendimentoFinalizado && atendimentoIniciado && user?.id) {
        // Obter o estado atual do localStorage para preservar a data de início
        const estadoSalvo = localStorage.getItem(`atendimento_${user.id}`);
        let iniciadoEm = new Date().toISOString(); // Valor padrão
        
        if (estadoSalvo) {
          try {
            const dadosAnteriores = JSON.parse(estadoSalvo);
            // Preservar a hora original de início
            if (dadosAnteriores.iniciadoEm) {
              iniciadoEm = dadosAnteriores.iniciadoEm;
            }
          } catch (error) {
            console.error('Erro ao ler dados salvos:', error);
          }
        }
        
        // Atualizar o localStorage com os dados mais recentes
        const dados = {
          senha: senhaChamada,
          iniciado: atendimentoIniciado,
          finalizado: atendimentoFinalizado,
          iniciadoEm: iniciadoEm,
          tempoDecorrido: tempoAtendimento,
          timestamp: new Date().toISOString()
        };
        
        // Salvar no localStorage de forma síncrona
        localStorage.setItem(`atendimento_${user.id}`, JSON.stringify(dados));
      }
    };
    
    // Registrar o evento
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Limpar o evento quando o componente for desmontado
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [senhaChamada, atendimentoIniciado, atendimentoFinalizado, user, tempoAtendimento]);

  // Efeito para atualizar o guichê sempre que o usuário for alterado
  useEffect(() => {
    if (user && user.guiche) {
      setGuicheSelecionado(user.guiche);
    }
  }, [user, setGuicheSelecionado]);
};

export default useDashboardEffects; 